'use client';
import React, { useState, useEffect } from 'react';
import BoardsGrid from './BoardsGrid';
import BoardDetail from './BoardDetail';
import CreateBoardModal from './CreateBoardModal';
import AuthGateModal from '@/components/AuthGateModal';
import { Plus, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

export interface Board {
  id: string;
  title: string;
  description: string;
  coverPhotos: string[];
  placeCount: number;
  isPublic: boolean;
  cafeIds: string[];
}

export default function SimpanClient() {
  const { user, loading: authLoading } = useAuth();
  const supabase = createClient();
  const router = useRouter();
  const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }

    const fetchBoards = async () => {
      // Get distinct collections from saved_places
      const { data, error } = await supabase
        .from('saved_places')
        .select(`id, coffee_shop_id, collection_name, coffee_shops(image_url)`)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.log('Boards fetch error:', error.message);
        setLoading(false);
        return;
      }

      // Group by collection_name
      const collectionMap: Record<string, { cafeIds: string[]; coverPhotos: string[] }> = {};
      (data || []).forEach((item: any) => {
        const name = item.collection_name || 'Favorit';
        if (!collectionMap[name]) collectionMap[name] = { cafeIds: [], coverPhotos: [] };
        collectionMap[name].cafeIds.push(item.coffee_shop_id);
        if (item.coffee_shops?.image_url && collectionMap[name].coverPhotos.length < 4) {
          collectionMap[name].coverPhotos.push(item.coffee_shops.image_url);
        }
      });

      const mappedBoards: Board[] = Object.entries(collectionMap).map(([name, val], i) => ({
        id: `collection-${i}`,
        title: name,
        description: '',
        coverPhotos: val.coverPhotos,
        placeCount: val.cafeIds.length,
        isPublic: true,
        cafeIds: val.cafeIds,
      }));

      setBoards(mappedBoards);
      setLoading(false);
    };

    fetchBoards();
  }, [user, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 size={28} className="animate-spin text-accent" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-4">🔖</div>
          <h2 className="text-xl font-800 text-primary mb-2">Login untuk lihat koleksi</h2>
          <p className="text-sm text-muted-foreground mb-6">Simpan cafe favorit ke board koleksi pribadimu!</p>
          <button onClick={() => router.push('/sign-up-login-screen')} className="btn-primary w-full">Masuk / Daftar</button>
        </div>
        <AuthGateModal isOpen={authOpen} onClose={() => setAuthOpen(false)} action="melihat koleksi tersimpan" />
      </div>
    );
  }

  const handleCreateBoard = async (title: string, description: string, isPublic: boolean) => {
    // Creating a board = creating a new collection name placeholder
    const newBoard: Board = {
      id: `board-${Date.now()}`,
      title,
      description,
      coverPhotos: [],
      placeCount: 0,
      isPublic,
      cafeIds: [],
    };
    setBoards((prev) => [newBoard, ...prev]);
    setCreateOpen(false);
  };

  if (selectedBoard) {
    return (
      <BoardDetail
        board={selectedBoard}
        onBack={() => setSelectedBoard(null)}
      />
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 pt-6 pb-16">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-800 text-primary">🔖 Koleksiku</h1>
          <p className="text-sm text-muted-foreground mt-1">
            <span className="font-700 text-foreground font-tabular">{boards.length}</span> board ·{' '}
            <span className="font-700 text-foreground font-tabular">{boards.reduce((a, b) => a + b.placeCount, 0)}</span> tempat tersimpan
          </p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="btn-primary text-sm">
          <Plus size={16} />
          Buat Board
        </button>
      </div>

      <BoardsGrid boards={boards} onSelectBoard={setSelectedBoard} />

      <CreateBoardModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreateBoard}
      />
    </div>
  );
}